import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { countryData } from "@/data/countryData";

const dialOptions = countryData
  .map(c => ({ code: c.code, dial: c.phone, flag: c.flag, name: c.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

function getDialCode(countryCode) {
  const country = countryData.find(c => c.code === countryCode);
  return country ? { dial: country.phone, flag: country.flag } : { dial: "+91", flag: "\ud83c\uddee\ud83c\uddf3" };
}

export function getPhoneWithCode(phone, countryCode, customDial) {
  const dial = customDial || getDialCode(countryCode).dial;
  if (!phone) return "";
  const clean = phone.replace(/^\+\d+\s*/, "").trim();
  return `${dial} ${clean}`;
}

export default function PhoneInput({ value, onChange, countryCode = "in", className = "", testId = "phone-input", placeholder = "Phone Number *", onDialChange }) {
  const initial = getDialCode(countryCode);
  const [selectedDial, setSelectedDial] = useState(initial.dial);
  const [selectedFlag, setSelectedFlag] = useState(initial.flag);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const d = getDialCode(countryCode);
    setSelectedDial(d.dial);
    setSelectedFlag(d.flag);
  }, [countryCode]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filtered = search
    ? dialOptions.filter(o => o.name.toLowerCase().includes(search.toLowerCase()) || o.dial.includes(search))
    : dialOptions;

  const handleSelect = (opt) => {
    setSelectedDial(opt.dial);
    setSelectedFlag(opt.flag);
    setOpen(false);
    setSearch("");
    onDialChange?.(opt.dial);
  };

  return (
    <div className={`flex items-center gap-0 relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 bg-white/10 border border-white/10 border-r-0 rounded-l-lg px-2 py-2.5 sm:py-3 text-sm text-white/70 whitespace-nowrap hover:bg-white/15 transition-colors cursor-pointer"
        data-testid={`${testId}-code`}
      >
        <span className="text-base leading-none">{selectedFlag}</span>
        <span className="text-xs sm:text-sm">{selectedDial}</span>
        <ChevronDown className="w-3 h-3 opacity-50" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 max-h-60 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50 overflow-hidden" data-testid={`${testId}-dropdown`}>
          <div className="p-2 border-b border-zinc-700">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search country..."
              className="w-full bg-white/5 border border-zinc-700 rounded px-2.5 py-1.5 text-white text-sm placeholder:text-zinc-500 outline-none focus:border-purple-500/50"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filtered.map(opt => (
              <button
                key={opt.code}
                type="button"
                onClick={() => handleSelect(opt)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/10 transition-colors text-left ${opt.dial === selectedDial ? "bg-purple-500/20 text-purple-300" : "text-white/80"}`}
                data-testid={`${testId}-option-${opt.code}`}
              >
                <span className="text-base">{opt.flag}</span>
                <span className="flex-1 truncate">{opt.name}</span>
                <span className="text-xs text-zinc-400">{opt.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-sm text-zinc-500">No countries found</p>
            )}
          </div>
        </div>
      )}

      <input
        type="tel"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full bg-white/5 border border-white/10 rounded-r-lg rounded-l-none p-2.5 sm:p-3 text-white placeholder:text-white/30 focus:border-purple-500/50 outline-none text-sm"
        data-testid={testId}
        required
      />
    </div>
  );
}
